import { FC, useState, useEffect, useRef } from "react";

import { formatDate } from "utilities/DateFormatter";
import CalendarIcon from "assets/icons/CalendarIcon";
import { SupportedLang } from "config/constants";
import ArrowLeftIcon from "assets/icons/ArrowLeftIcon";
import ArrowRightIcon from "assets/icons/ArrowRightIcon";

interface MonthPickerInputProps {
  value: Date;
  onChange: (date: Date) => void;
  disable?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const MonthPickerInput: FC<MonthPickerInputProps> = ({
  value,
  onChange,
  disable = false,
  minDate,
  maxDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapperClasses = disable
    ? "cursor-not-allowed bg-gray-100 text-[#9CA3AF]"
    : "cursor-pointer";
  const inputClasses = disable
    ? "bg-gray-100 border border-[#D1D5DB]"
    : "bg-white border border-[#9B9B9B]";

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setViewDate(value);
  }, [value]);

  const changeCurrentYear = (step: number) => {
    const date = new Date(viewDate);
    date.setFullYear(viewDate.getFullYear() + step);
    if (minDate && date.getFullYear() < minDate.getFullYear()) {
      return;
    }
    if (maxDate && date.getFullYear() > maxDate.getFullYear()) {
      return;
    }
    setViewDate(date);
  };

  const renderPreviousYearButton = () => {
    let isVisible = false;
    if (
      !minDate ||
      (minDate && viewDate.getFullYear() > minDate.getFullYear())
    ) {
      isVisible = true;
    }
    return (
      <button
        type="button"
        className={`rounded p-1 transition-colors hover:bg-gray-200 active:bg-gray-300 ${
          isVisible ? "visible" : "invisible"
        }`}
        onClick={() => changeCurrentYear(-1)}
      >
        <ArrowLeftIcon width={24} height={24} color={"#9B9B9B"} />
      </button>
    );
  };

  const renderNextYearButton = () => {
    let isVisible = false;
    if (
      !maxDate ||
      (maxDate && viewDate.getFullYear() < maxDate.getFullYear())
    ) {
      isVisible = true;
    }
    return (
      <button
        type="button"
        className={`rounded p-1 transition-colors hover:bg-gray-200 active:bg-gray-300 ${
          isVisible ? "visible" : "invisible"
        }`}
        onClick={() => changeCurrentYear(1)}
      >
        <ArrowRightIcon width={24} height={24} color={"#9B9B9B"} />
      </button>
    );
  };

  const renderMonthOptions = () => {
    const monthButtons = [];
    for (let i = 0; i < 12; i++) {
      const temp = new Date(viewDate);
      temp.setMonth(i);
      const lowerBoundCondition = minDate && temp.getTime() < minDate.getTime();
      const upperBoundCondition = maxDate && temp.getTime() > maxDate.getTime();
      const isDisabled = lowerBoundCondition || upperBoundCondition;
      const isSelected =
        i === value.getMonth() &&
        viewDate.getFullYear() === value.getFullYear();
      monthButtons.push(
        <button
          key={temp.getMonth()}
          type="button"
          className={`p-2 border border-[#f8f8f8] rounded-lg ${
            isSelected ? "bg-primary text-white" : ""
          } ${isDisabled ? "opacity-50" : "opacity-100"}`}
          onClick={() => {
            const newDate = new Date(viewDate);
            newDate.setMonth(i);
            onChange(newDate);
            setIsOpen(false); // Close dropdown after selection
          }}
          disabled={isDisabled}
        >
          {formatDate(temp, {
            [SupportedLang.ja]: "Mo",
            [SupportedLang.en]: "MMMM",
          })}
        </button>
      );
    }
    return monthButtons;
  };

  return (
    <div
      ref={dropdownRef}
      className={`w-full h-full space-y-1 relative ${wrapperClasses}`}
    >
      <div
        className={`w-full h-full flex justify-between items-center p-3 rounded-lg ${inputClasses}`}
        onClick={() => !disable && setIsOpen(!isOpen)}
      >
        <p className="text-base">
          {formatDate(value, {
            [SupportedLang.ja]: "yo Mo",
            [SupportedLang.en]: "yyyy MMMM",
          })}
        </p>
        <CalendarIcon
          width={20}
          height={20}
          color={disable ? "#9CA3AF" : "#6B7280"}
        />
      </div>
      {isOpen && (
        <div className="w-full bg-white rounded-lg p-2 absolute z-40 shadow-lg">
          <div className="flex justify-center space-x-3">
            {renderPreviousYearButton()}
            <h3 className="font-semibold text-xl">
              {formatDate(viewDate, {
                [SupportedLang.ja]: "yo",
                [SupportedLang.en]: "yyyy",
              })}
            </h3>
            {renderNextYearButton()}
          </div>
          <div className="w-full grid grid-cols-3">{renderMonthOptions()}</div>
        </div>
      )}
    </div>
  );
};

export default MonthPickerInput;
